import React from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ModernLoader from '../../components/ModernLoader';
// Link replaced by SourceLink
import SourceLink from '../../components/SourceLink';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useDevices } from '../../hooks/useDevices';
import type { UnifiedDeviceInfo } from '../../types/device';
import { postSpeedTest, fetchUserDetails } from '../../services/api';
import { useParams, useNavigate } from 'react-router-dom';
import { useDeviceCount } from '../../contexts/DeviceCountContext';
import { useQuery } from '@tanstack/react-query';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

export default function SpeedTestPage() {
  const { tgmaxxCount } = useDeviceCount();
  
  // Get user details to access user tenants
  const { data: userDetails } = useQuery({
    queryKey: ['user', 'details'],
    queryFn: fetchUserDetails,
    staleTime: 1000 * 60 * 5,
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Extract TGMaxx tenant only
  const userTenants = React.useMemo(() => {
    if (!userDetails?.userTenants) return [];
    return userDetails.userTenants.filter((ut: any) =>
      ut?.tenant?.name && ut.tenant.name.toLowerCase() === 'tgmaxx'
    );
  }, [userDetails]);
  
  const [page, setPage] = React.useState<number>(1);
  const [pageInput, setPageInput] = React.useState<string>(String(page));
  const [limit, setLimit] = React.useState<number>(20);
  const [search, setSearch] = React.useState<string>('');
  
  // Initialize selectedTenant - will be set when userTenants loads
  const [selectedTenant, setSelectedTenant] = React.useState<string>('');

  // Update selectedTenant when userTenants loads
  React.useEffect(() => {
    if (userTenants.length > 0) {
      // If selectedTenant is empty or not in the list, set to first available tenant
      if (!selectedTenant || !userTenants.find((ut: any) => ut.tenant.name === selectedTenant)) {
        setSelectedTenant(userTenants[0].tenant.name);
      }
    }
  }, [userTenants, selectedTenant]);
  
  const { data, isLoading } = useDevices({
    page, 
    limit, 
    tenant: selectedTenant || 'TGMaxx', // TGMaxx only
    search: search.trim() || undefined,
    enabled: selectedTenant !== '' // Only enable when tenant is selected
  });
  const typedData = data as { total: number; devices: UnifiedDeviceInfo[] } | undefined;

  // Determine if we're in search mode
  const isSearchMode = Boolean(search && search.trim().length > 0);

  // selectedDevice derived from URL sn param
  const { sn } = useParams<{ sn?: string }>();
  const navigate = useNavigate();

  const selectedDevice = React.useMemo<UnifiedDeviceInfo | null>(() => {
    if (!sn || !typedData?.devices) return null;
    const decoded = decodeURIComponent(sn);
    return typedData.devices.find((d) => (d.sn ?? d.id) === decoded) ?? null;
  }, [sn, typedData]);
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState<any>(null);
  const [downloadUrl, setDownloadUrl] = React.useState<string>('https://speed.hetzner.de/100MB.bin');
  const [snack, setSnack] = React.useState<{ open: boolean; message: string; severity: 'success'|'error' }>({ open: false, message: '', severity: 'success' });

  // clear prior result when sn changes
  React.useEffect(() => {
    setResult(null);
  }, [sn]);

  // Handle tenant change. Reset to page 1 when tenant changes.
  const handleTenantChange = React.useCallback((event: React.SyntheticEvent, newValue: string) => {
    setSelectedTenant(newValue);
    setPage(1);
  }, []);

  // Handle search functionality
  const handleSearch = React.useCallback((query: string) => {
    setSearch(query);
    setPage(1); // Reset to first page when searching
  }, []);

  // Calculate total items using stored TGMaxx count when applicable
  const totalItems = isSearchMode 
    ? (typedData?.total || 0) // Use search results total
    : selectedTenant === 'TGMaxx' && tgmaxxCount !== null
      ? tgmaxxCount
      : (typedData?.total ?? 0);
  const totalPages = Math.max(1, Math.ceil(totalItems / limit));

  const handleRun = async () => {
    if (!selectedDevice) { setSnack({ open: true, message: 'Select a device first', severity: 'error' }); return; }
    setLoading(true); setResult(null);
    try {
      const payload = { mac: selectedDevice.mac, downloadUrl };
      const res = await postSpeedTest(payload);
      setResult(res);
      setSnack({ open: true, message: 'Speed test triggered', severity: 'success' });
    } catch (err: any) {
      setSnack({ open: true, message: err?.message ?? 'Speed test failed', severity: 'error' });
    } finally { setLoading(false); }
  };

  if (!selectedDevice) {
    return (
      <>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h5" className="page-title">
                {isSearchMode ? `Speed Test - Search Results` : 'Speed Test'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isSearchMode 
                  ? `Found: ${totalItems}` 
                  : selectedTenant === 'TGMaxx' && tgmaxxCount !== null 
                    ? `Total devices: ${tgmaxxCount}` 
                    : `Total devices: ${typedData?.total ?? '—'}`}
              </Typography>
        </Box>
        <Paper sx={{ p: 1, borderRadius: 2, border: '2px solid rgba(0,0,0,0.06)' }} elevation={3}>
          {/* Tenant Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            {userTenants.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                TGMaxx tenant access is required to use maintenance tools.
              </Typography>
            ) : (
              <Tabs 
                value={selectedTenant} 
                onChange={handleTenantChange} 
                aria-label="tenant tabs"
                sx={{
                  '& .MuiTab-root': {
                    opacity: isSearchMode ? 0.5 : 1,
                    pointerEvents: isSearchMode ? 'none' : 'auto',
                    cursor: isSearchMode ? 'not-allowed' : 'pointer'
                  }
                }}
              >
                {userTenants.map((ut: any) => (
                  <Tab 
                    key={ut.tenant.name} 
                    label={isSearchMode 
                      ? `${ut.tenant.displayName || ut.tenant.name} (Search Mode)` 
                      : (ut.tenant.displayName || ut.tenant.name)}
                    value={ut.tenant.name}
                    disabled={isSearchMode}
                    sx={{ 
                      textTransform: 'none', 
                      fontWeight: 600,
                      color: isSearchMode ? 'text.disabled' : 'inherit'
                    }}
                  />
                ))}
              </Tabs>
            )}
            {isSearchMode && (
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}
              >
                Showing combined results from all tenants
              </Typography>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1">Devices</Typography>
            {/* Search Bar */}
            <TextField
              size="small"
              placeholder="Search SN or MAC"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearch(search);
              }}
              sx={{ width: 280 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {search ? (
                      <IconButton 
                        size="small" 
                        aria-label="clear" 
                        onClick={() => handleSearch('')}
                      >
                        <CloseIcon />
                      </IconButton>
                    ) : (
                      <IconButton 
                        size="small" 
                        aria-label="search" 
                        onClick={() => handleSearch(search)}
                      >
                        <SearchIcon />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <TableContainer sx={{ mt: 1, p: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>SN</TableCell>
                  <TableCell>MAC</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell>Online</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}><ModernLoader size={28} /></TableCell></TableRow>
                ) : (typedData?.devices ?? []).map((d) => (
                  <TableRow key={d.id} hover onClick={(e) => {
                    try {
                      const sel = typeof window !== 'undefined' && window.getSelection ? window.getSelection()?.toString().trim() : '';
                      if (sel) return; // Don't navigate if user is selecting text
                    } catch (err) {
                      // ignore selection read errors and proceed with click
                    }
                    navigate(`/maintenance/speedtest/${encodeURIComponent(d.sn ?? d.id)}`);
                  }} sx={{ cursor: 'pointer' }}>
                    <TableCell>{d.sn ?? d.id}</TableCell>
                    <TableCell>{d.mac ?? '\u2014'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <SourceLink source={d.source} onClick={(e) => e.stopPropagation()} />
                    </TableCell>
                    <TableCell><span className={`app-status-bubble ${d.online ? 'status-green' : 'status-grey'}`} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel id="speed-per-page-label">Per page</InputLabel>
                <Select labelId="speed-per-page-label" value={limit} label="Per page" onChange={(e) => { const v = Number((e.target as any).value) || 20; setLimit(v); setPage(1); }} MenuProps={{ PaperProps: { sx: { bgcolor: '#ffffff' } } }}>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary">
                {isSearchMode 
                  ? `Found: ${totalItems}` 
                  : selectedTenant === 'TGMaxx' && tgmaxxCount !== null 
                    ? `Total: ${tgmaxxCount}` 
                    : `Total: ${typedData?.total ?? '—'}`}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Pagination count={totalPages} page={page} onChange={(_e, v) => setPage(v)} color="primary" />
              <TextField size="small" value={pageInput} onChange={(e) => setPageInput(e.target.value)} onBlur={() => { const v = Number(pageInput) || 1; const target = Math.max(1, Math.min(totalPages, Math.floor(v))); if (target !== page) setPage(target); setPageInput(String(target)); }} onKeyDown={(e) => { if (e.key === 'Enter') { const v = Number(pageInput) || 1; const target = Math.max(1, Math.min(totalPages, Math.floor(v))); if (target !== page) setPage(target); setPageInput(String(target)); } }} inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', style: { textAlign: 'center', width: 56 } }} aria-label="Go to page" />
            </Box>
          </Box>
        </Paper>
      </>
    );
  }

  return (
    <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
  <Button variant="outlined" startIcon={<ArrowBackIcon />} size="small" onClick={() => { setResult(null); navigate('/maintenance/speedtest'); }}>
          Back
        </Button>
        <Typography variant="h5">Speed Test — {selectedDevice.sn}</Typography>
      </Box>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Download URL" value={downloadUrl} onChange={(e) => setDownloadUrl(e.target.value)} fullWidth />
          <Button variant="contained" onClick={handleRun} disabled={loading}>{loading ? 'Running...' : 'Run Speed Test'}</Button>
          <Typography variant="subtitle2">Result</Typography>
          <Paper sx={{ p: 1, mt: 1, maxHeight: 360, overflow: 'auto' }}>{result ? <TextField multiline fullWidth minRows={6} value={JSON.stringify(result, null, 2)} /> : <Typography variant="body2" color="text.secondary">No result yet.</Typography>}</Paper>
        </Box>
      </Paper>
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack((s) => ({ ...s, open: false }))}><Alert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.severity} sx={{ width: '100%' }}>{snack.message}</Alert></Snackbar>
    </>
  );
}
